import { useState, useRef } from 'react';
import { isEqual } from 'lodash';
import { Document, Page, pdfjs } from "react-pdf";
import {
  ZoomIn,
  ZoomOut,
  Add,
  Remove
} from '@mui/icons-material';
import { IconButton, useMediaQuery, useTheme } from '@mui/material';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const INIT_SCALE = 0.95
const INIT_POSITION = { x: 0, y: 0 }
const MAX_SCALE = 1.45
const MIN_SCALE = 0.55

const ControlsOverlay = ({ scale, position, setScale, setPosition }) => {
  let ZoomStatueIcon = null

  const zoomIn = (value) => {
    if (scale >= MAX_SCALE) return

    const newScale = value ? value : 0.25
    setScale(prev => Math.min(MAX_SCALE, prev + newScale))
  }

  const zoomOut = (value) => {
    if (scale <= MIN_SCALE) return

    const newScale = value ? value : 0.25
    setScale(prev => Math.max(MIN_SCALE, prev - newScale))
  }

  const resetZoom = (value) => {
    const newScale = value ? value : INIT_SCALE

    setScale(newScale)
    setPosition(INIT_POSITION)
  }

  if (isEqual(position, INIT_POSITION) && scale === INIT_SCALE) {
    ZoomStatueIcon = (
      <IconButton
        aria-label="zoom in"
        sx={style.controlOverlay.iconButton}
        onClick={() => zoomIn(0.5)}
      >
        <ZoomIn />
      </IconButton>
    )
  } else {
    ZoomStatueIcon = (
      <IconButton
        aria-label="reset"
        sx={style.controlOverlay.iconButton}
        onClick={() => resetZoom()}
      >
        <ZoomOut />
      </IconButton>
    )
  }

  return (
    <div style={style.controlOverlay.overlayDiv}>
      <div style={style.controlOverlay.controlDiv}>
        <IconButton
          aria-label="zoom out"
          disabled={scale <= MIN_SCALE}
          sx={style.controlOverlay.iconButton}
          onClick={() => zoomOut()}
        >
          <Remove/>
        </IconButton>
        { ZoomStatueIcon }
        <IconButton
          aria-label="zoom in"
          disabled={scale >= MAX_SCALE}
          sx={style.controlOverlay.iconButton}
          onClick={() => zoomIn()}
        >
          <Add />
        </IconButton>
      </div>
    </div>
  )
}

const PDFViewer = ({ pdfUrl }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isLoading, setIsLoading] = useState(true);
  const [numPages, setNumPages] = useState(null);

  const [scale, setScale] = useState(INIT_SCALE)
  const [position, setPosition] = useState(INIT_POSITION)
  const [dragging, setDragging] = useState(false)

  const dragStart = useRef(INIT_POSITION)

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setIsLoading(false)
  }

  const handleMouseDown = (e) => {
    setDragging(true)
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    }
  }

  const handleMouseMove = (e) => {
    if (!dragging) return
    setPosition({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y
    })
  }

  const handleMouseUp = () => setDragging(false)

  return (
    <div
      style={{ overflow: 'hidden', cursor: dragging ? 'grabbing' : 'grab' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          transformOrigin: 'center top',
        }}
      >
        <Document
          file={pdfUrl}
          onLoadError={(e) => console.error('pdf viewer error: ', e)}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          {numPages > 0 &&
            Array.from({ length: numPages }, (_, index) => {
              const pageNumber = index + 1

              return (
                <Page
                  key={pageNumber}
                  pageNumber={pageNumber}
                  renderTextLayer={false}
                  width={isMobile ? window.innerWidth * 0.75 : window.innerWidth * 0.5}
                  className={pageNumber !== numPages ? 'pb-4' : ''}
                />
              )
            })}
        </Document>
      </div>
      { !isLoading && <ControlsOverlay scale={scale} position={position} setScale={setScale} setPosition={setPosition} />}
    </div>
  )
}

export default PDFViewer

const style = {
  controlOverlay: {
    overlayDiv: {
      position: 'absolute',
      bottom: '4rem',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'rgba(60, 60, 60, 0.9)',
      padding: '0.25rem 0.5rem',
      borderRadius: '16px',
      display: 'flex'
    },
    controlDiv: {
      display: 'flex',
      gap: '10px',
      color: 'white',
    },
    iconButton: {
      color: 'white',
    }
  },
}
